import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Gavel, Clock, Users, TrendingUp, Filter, ChevronDown, Eye } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const STATUS_LABELS = {
  live: { label: 'En vivo', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
  ending_soon: { label: 'Por terminar', color: 'bg-red-500/20 text-red-300 border-red-500/30 animate-pulse' },
  scheduled: { label: 'Programada', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  awaiting_payment: { label: 'Esperando pago', color: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  completed: { label: 'Completada', color: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/30' },
  canceled: { label: 'Cancelada', color: 'bg-zinc-600/20 text-zinc-400 border-zinc-600/30' },
};

function AuctionCountdown({ endTime }) {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const calc = () => {
      const end = new Date(endTime);
      const now = new Date();
      const diff = end - now;
      if (diff <= 0) { setTimeLeft('Terminada'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      if (d > 0) setTimeLeft(`${d}d ${h}h ${m}m`);
      else if (h > 0) setTimeLeft(`${h}h ${m}m ${s}s`);
      else setTimeLeft(`${m}m ${s}s`);
    };
    calc();
    const interval = setInterval(calc, 1000);
    return () => clearInterval(interval);
  }, [endTime]);

  return <span data-testid="auction-countdown">{timeLeft}</span>;
}

function AuctionCard({ auction }) {
  const statusInfo = STATUS_LABELS[auction.status] || STATUS_LABELS.live;
  const img = auction.images?.[0] || '/placeholder-animal.jpg';
  const formatPrice = (p) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(p);

  return (
    <Link to={`/subastas/${auction.id}`} className="group" data-testid={`auction-card-${auction.id}`}>
      <div className="glass-card overflow-hidden hover:border-white/15 transition-all duration-300 hover:-translate-y-1">
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <img src={img} alt={auction.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute top-3 left-3">
            <span className={`px-3 py-1 rounded-full text-xs font-bold border backdrop-blur-md ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
          </div>
          {(auction.status === 'live' || auction.status === 'ending_soon') && (
            <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-md rounded-lg px-3 py-1.5 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs font-mono text-white">
                <AuctionCountdown endTime={auction.end_time} />
              </span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-4 space-y-3">
          <h3 className="text-base font-medium text-zinc-100 truncate group-hover:text-white transition-colors">
            {auction.title}
          </h3>

          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs text-zinc-500 block">Puja actual</span>
              <span className="text-lg font-semibold text-zinc-100 font-[Outfit]">
                {auction.current_bid > 0 ? formatPrice(auction.current_bid) : formatPrice(auction.start_price)}
              </span>
              {auction.current_bid === 0 && (
                <span className="text-xs text-zinc-500 ml-1">inicio</span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-zinc-500">
              <span className="flex items-center gap-1">
                <Gavel className="w-3.5 h-3.5" />
                {auction.bid_count}
              </span>
              <span className="flex items-center gap-1">
                <Eye className="w-3.5 h-3.5" />
                {auction.watcher_count}
              </span>
            </div>
          </div>

          <div className="text-xs text-zinc-500">
            por <span className="text-zinc-400">{auction.seller_name}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function AuctionsPage() {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('ending_soon');
  const [statusFilter, setStatusFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadAuctions();
  }, [sort, statusFilter, page]);

  const loadAuctions = async () => {
    try {
      const data = await api.getAuctions({ sort, status: statusFilter || undefined, page, limit: 12 });
      setAuctions(data.auctions);
      setTotal(data.total);
    } catch (e) {
      toast.error('Error cargando subastas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] pt-24 pb-16" data-testid="auctions-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                <Gavel className="w-5 h-5 text-amber-400" />
              </div>
              <h1 className="text-3xl md:text-4xl font-medium text-zinc-100 font-[Outfit]">
                Subastas
              </h1>
            </div>
            <p className="text-zinc-500 text-sm ml-[52px]">
              Puja por ejemplares exclusivos de criadores verificados
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-8">
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-sm text-zinc-300 focus:outline-none focus:border-purple-500/50"
            data-testid="auction-sort"
          >
            <option value="ending_soon">Por terminar</option>
            <option value="newest">Más recientes</option>
            <option value="price_high">Mayor precio</option>
            <option value="price_low">Menor precio</option>
            <option value="most_bids">Más pujas</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-sm text-zinc-300 focus:outline-none focus:border-purple-500/50"
            data-testid="auction-status-filter"
          >
            <option value="">Todas activas</option>
            <option value="live">En vivo</option>
            <option value="ending_soon">Por terminar</option>
            <option value="scheduled">Programadas</option>
            <option value="awaiting_payment">Esperando pago</option>
            <option value="completed">Completadas</option>
          </select>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="glass-card overflow-hidden">
                <div className="aspect-[4/3] skeleton" />
                <div className="p-4 space-y-3">
                  <div className="h-5 w-3/4 skeleton rounded" />
                  <div className="h-6 w-1/2 skeleton rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : auctions.length === 0 ? (
          <div className="text-center py-20">
            <Gavel className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl text-zinc-400 mb-2">No hay subastas activas</h3>
            <p className="text-zinc-600 text-sm">Las subastas aparecerán aquí cuando los criadores publiquen nuevas.</p>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="auctions-grid">
              {auctions.map(auction => (
                <AuctionCard key={auction.id} auction={auction} />
              ))}
            </div>
            {total > 12 && (
              <div className="flex justify-center mt-10 gap-2">
                <Button
                  variant="outline"
                  className="border-white/10 text-zinc-400"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Anterior
                </Button>
                <span className="flex items-center px-4 text-sm text-zinc-500">
                  Página {page}
                </span>
                <Button
                  variant="outline"
                  className="border-white/10 text-zinc-400"
                  disabled={auctions.length < 12}
                  onClick={() => setPage(p => p + 1)}
                >
                  Siguiente
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
